import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

// ── Request/Response types for Cloud Functions ──

export interface InitiateParentConsentRequest {
  childDisplayName: string;
  parentEmail: string;
  profileId: string;
  dateOfBirth: string; // ISO date string
}

export interface InitiateParentConsentResponse {
  consentPageUrl: string;
  token: string;
}

export interface RecordConsentRequest {
  profileId: string;
  generalConsent: boolean;
  aiConsent: boolean;
  schoolConsent: boolean;
}

export interface SendParentEmailRequest {
  type:
    | 'welcome'
    | 'day7_progress'
    | 'day11_value';
  parentEmail: string;
  childName: string;
  profileId: string;
}

export interface CreateProfileRequest {
  uid: string;
  explorerName: string;
  dateOfBirth: string; // ISO date string
  signupPlatform: 'web';
  subscriptionStatus?: 'pending_consent';
  parentEmail?: string;
}

export interface CreateProfileResponse {
  profileId: string;
}

export interface ValidateConsentTokenRequest {
  token: string;
}

export interface ValidateConsentTokenResponse {
  valid: boolean;
  childDisplayName: string;
  profileId: string;
}

// ── Typed callable wrappers ──

export const initiateParentConsent = httpsCallable<
  InitiateParentConsentRequest,
  InitiateParentConsentResponse
>(functions, 'initiateParentConsent');

export const recordConsent = httpsCallable<RecordConsentRequest, void>(
  functions,
  'recordConsent',
);

export const sendParentEmail = httpsCallable<SendParentEmailRequest, void>(
  functions,
  'sendParentEmail',
);

export const createProfile = httpsCallable<CreateProfileRequest, CreateProfileResponse>(
  functions,
  'createProfile',
);

export interface CheckEmailAvailabilityRequest {
  email: string;
}

export interface CheckEmailAvailabilityResponse {
  available: true;
}

export const checkEmailAvailability = httpsCallable<
  CheckEmailAvailabilityRequest,
  CheckEmailAvailabilityResponse
>(functions, 'checkEmailAvailability');

export const validateConsentToken = httpsCallable<
  ValidateConsentTokenRequest,
  ValidateConsentTokenResponse
>(functions, 'validateConsentToken');

export interface ClearPendingSignUpResponse {
  cleared: true;
}

export const clearPendingSignUp = httpsCallable<Record<string, never>, ClearPendingSignUpResponse>(
  functions,
  'clearPendingSignUp',
);

export interface ResendConsentEmailRequest {
  token: string;
}

export const resendConsentEmail = httpsCallable<ResendConsentEmailRequest, { newToken?: string }>(
  functions,
  'resendConsentEmail',
);

export interface UpdateConsentEmailRequest {
  profileId: string;
  newParentEmail: string;
}

export interface UpdateConsentEmailResponse {
  token: string;
  consentPageUrl: string;
}

export const updateConsentEmail = httpsCallable<UpdateConsentEmailRequest, UpdateConsentEmailResponse>(
  functions,
  'updateConsentEmail',
);

export interface SendPasswordResetEmailRequest {
  email: string;
}

export const sendPasswordResetEmail = httpsCallable<SendPasswordResetEmailRequest, void>(
  functions,
  'sendPasswordResetEmail',
);

// ── Phase 2: Onboarding & Plan Management ──

export interface UpdateProfileRequest {
  profileId: string;
  fields: Record<string, unknown>;
}

export interface CreateBusinessPlanRequest {
  profileId: string;
  explorerName: string;
}

export interface CreateBusinessPlanResponse {
  planId: string;
}

export const updateProfile = httpsCallable<UpdateProfileRequest, void>(
  functions,
  'updateProfile',
);

export interface CompleteSignUpRequest {
  profileId: string;
}

export interface CompleteSignUpResponse {
  success: true;
}

export const completeSignUp = httpsCallable<CompleteSignUpRequest, CompleteSignUpResponse>(
  functions,
  'completeSignUp',
);

export const createBusinessPlan = httpsCallable<
  CreateBusinessPlanRequest,
  CreateBusinessPlanResponse
>(functions, 'createBusinessPlan');

// ── AI Sidekick ──

export interface CallAiSidekickRequest {
  profileId: string;
  planId: string;
  stageId: string;
  inputText: string;
  conversationHistory: Array<{ role: 'user' | 'assistant'; text: string }>;
  tonePreference: string;
  engagementMode: string;
  stageContext: string;
}

export interface CallAiSidekickResponse {
  responseText: string;
  tokensUsed: number;
  newEngagementMode?: string;
  inputCategory?: string;
}

export interface ModerateContentRequest {
  text: string;
  userId: string;
}

export interface ModerateContentResponse {
  safe: boolean;
  strikeLevel: number; // 0 = safe, 1 = mild, 2 = moderate, 3 = severe
  reason?: string;
}

export const callAiSidekick = httpsCallable<
  CallAiSidekickRequest,
  CallAiSidekickResponse
>(functions, 'callAiSidekick');

export const moderateContent = httpsCallable<
  ModerateContentRequest,
  ModerateContentResponse
>(functions, 'moderateContent');

// Generic OpenAI callable — no sidekick-specific logic, no token deduction.
export interface CallOpenAIRequest {
  prompt: string;
  systemPrompt?: string;
}

export interface CallOpenAIResponse {
  responseText: string;
}

export const callOpenAI = httpsCallable<
  CallOpenAIRequest,
  CallOpenAIResponse
>(functions, 'callOpenAI');

// ── Account Settings ──

export interface RenameExplorerNameRequest {
  profileId: string;
  newExplorerName: string;
}

export interface RenameExplorerNameResponse {
  success: true;
}

export const renameExplorerName = httpsCallable<
  RenameExplorerNameRequest,
  RenameExplorerNameResponse
>(functions, 'renameExplorerName');

export interface RequestEmailChangeRequest {
  profileId: string;
  newEmail: string;
}

export interface RequestEmailChangeResponse {
  sent: true;
}

export const requestEmailChange = httpsCallable<
  RequestEmailChangeRequest,
  RequestEmailChangeResponse
>(functions, 'requestEmailChange');

export interface ConfirmEmailChangeRequest {
  token: string;
}

export interface ConfirmEmailChangeResponse {
  success: true;
}

export const confirmEmailChange = httpsCallable<
  ConfirmEmailChangeRequest,
  ConfirmEmailChangeResponse
>(functions, 'confirmEmailChange');

// ── Payments & Account ──

export interface DeleteAccountRequest {
  profileId: string;
}

export interface ExportUserDataRequest {
  profileId: string;
}

export interface ExportUserDataResponse {
  downloadUrl: string;
  expiresAt: string;
}

export const deleteAccount = httpsCallable<DeleteAccountRequest, void>(
  functions,
  'deleteAccount',
);

// ── Under-13 Credentials ──

export interface FinalizeUnder13CredentialsRequest {
  profileId: string;
  password: string;
}

export const finalizeUnder13Credentials = httpsCallable<FinalizeUnder13CredentialsRequest, void>(
  functions,
  'finalizeUnder13Credentials',
);

export interface ResolveUnder13LoginRequest {
  explorerName: string;
  password: string;
}

export interface ResolveUnder13LoginResponse {
  customToken: string;
}

export const resolveUnder13Login = httpsCallable<
  ResolveUnder13LoginRequest,
  ResolveUnder13LoginResponse
>(functions, 'resolveUnder13Login');

export interface ParentResetChildPasswordRequest {
  profileId: string;
  newPassword: string;
}

export const parentResetChildPassword = httpsCallable<
  ParentResetChildPasswordRequest,
  { success: true }
>(functions, 'parentResetChildPassword');

export interface RevokeConsentRequest {
  profileId: string;
}

export const revokeConsent = httpsCallable<RevokeConsentRequest, { success: true }>(
  functions,
  'revokeConsent',
);

export const exportUserData = httpsCallable<
  ExportUserDataRequest,
  ExportUserDataResponse
>(functions, 'exportUserData');
